import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import operaCloudCompareService from '../services/operaCloudCompareService';
import useCredentialStep from './useCredentialStep';
import CredentialChip from './shared/CredentialChip';
import CredentialSelector from './shared/CredentialSelector';
import OperaCloudCompareEngine from './OperaCloudCompareEngine';
import './ProfileIATAPage.css';
import './OperaCloudCompare.css';
import { GitCompare } from 'lucide-react';
import Tabs from './shared/Tabs';
import ProductSupportTab, { SUPPORT_TAB, useSupportTab } from './support/ProductSupportTab';

// Opera Cloud v Opera Cloud — the working comparison screen for a PAID comparison. Loads the saved
// pairing (source → target OHIP instances, both editable) and hosts the compare/repair engine.

const OHIP_MARKETPLACE = { id: 37, name: 'Oracle OHIP' };

// The credential level for a side, derived from which owning-entity column is set.
const levelOf = (c, side) =>
    c[`${side}_credential_partner_id`] ? 'partner'
    : c[`${side}_credential_account_id`] ? 'account'
    : c[`${side}_credential_hotel_id`] ? 'hotel'
    : undefined;

// Build the instance payload the update endpoint expects from a credential-step controller.
const instancePayload = (cs) => {
    const p = cs.buildCredentialPayload();
    return {
        credential_id:     p.credential_id,
        account_id:        p.credential_account_id,
        hotel_id:          p.credential_hotel_id,
        partner_id:        p.credential_partner_id,
        hotel_id_override: cs.hotelIdOverride || null,
        overrides:         p.credential_overrides,
    };
};

const OperaCloudCompareDetailPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [tab, setTab] = useSupportTab('compare');   // Compare | Support
    const { selectedAccount, selectedPartner, isChainAdmin } = useAccount();
    const { isSuperAdmin, isPartner } = useAuth();

    const [comparison, setComparison] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(null);      // null | 'source' | 'target'
    const [pickerOpen, setPickerOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    // One credential-step controller for the row being edited. Seeded from the comparison's hotel.
    const cs = useCredentialStep({
        selectedMarketplace: OHIP_MARKETPLACE,
        selectedHotel: comparison ? { hotelid: comparison.hotel_id, accountid: comparison.account_id } : null,
        selectedAccount: comparison?.account_id || selectedAccount,
        selectedPartner,
        solePartnerId: selectedPartner,
        isSuperAdmin, isPartner, isChainAdmin,
    });

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await operaCloudCompareService.get(id);
            const c = res.data?.comparison;
            if (!c) { setError('Comparison not found'); return; }
            if (c.paid !== 'Y') { navigate(`/apps/opera-cloud-compare/${id}/pay`, { replace: true }); return; }
            setComparison(c);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load comparison');
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => { load(); }, [load]);
    // Load OHIP override defs (HOTELID etc.) once so editing a chain/partner-level cred prompts for them.
    useEffect(() => { cs.loadForMarketplace(); }, []); // eslint-disable-line

    const resetOverrides = () => {
        cs.setHotelIdOverride('');
        const fields = {};
        cs.overrideDefs.forEach(d => { fields[d.SETTINGNAME] = ''; });
        cs.setCredentialOverrides(fields);
    };

    const startEdit = (side) => { setEditing(side); setSaveError(null); cs.setSelectedCredential(null); resetOverrides(); setPickerOpen(true); };
    const cancelEdit = () => { setEditing(null); setPickerOpen(false); cs.setSelectedCredential(null); };

    const handleSelect = (cred, values) => {
        resetOverrides();
        cs.pickWithOverrides(cred, values);
        setPickerOpen(false);
    };

    const saveEdit = async () => {
        const err = cs.validate();
        if (err) { setSaveError(err.hotelIdOverride || err.connector || 'Please complete the credential'); return; }
        setSaving(true);
        setSaveError(null);
        try {
            await operaCloudCompareService.update(id, { [editing]: instancePayload(cs) });
            setEditing(null);
            await load();
        } catch (e) {
            setSaveError(e.response?.data?.message || e.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    // The picker itself is CredentialSelector (rendered in the editing row below) — it opens as part of
    // the side's edit lifecycle rather than owning its own state, which is what its `open` prop is for.

    // One editable Source/Target row: the current credential as a chip with a Change action, or the
    // in-progress edit (new chip, its per-hotel overrides asked in the picker, + Save/Cancel).
    const renderInstanceRow = (side, roleLabel) => {
        if (editing === side) {
            const credType = cs.selectedCredential?._entityType;
            const selAboveHotel = credType === 'account' || credType === 'partner';
            return (
                <div className="occ-instance-block">
                    <div className="occ-instance-row">
                        <div className="occ-instance-role">{roleLabel}</div>
                            <CredentialSelector
                                marketplaceId={OHIP_MARKETPLACE.id}
                                hotelId={comparison?.hotel_id}
                                accountId={comparison?.account_id || selectedAccount}
                                partnerId={selectedPartner}
                                value={cs.selectedCredential}
                                // Per-hotel fields for a chain/partner credential, asked in the picker (shared pattern);
                                // the row's own Save below then writes the side.
                                overridesInPicker
                                overrideDefs={cs.overrideDefs.length ? cs.overrideDefs : undefined}
                                overrides={cs.overrideValues}
                                overridesTitle="Per-Hotel Overrides"
                                onChange={(sel, values) => sel && handleSelect(sel, values)}
                                canEdit={isSuperAdmin}
                                title={null}
                                subtitle="Choose an existing OHIP credential from the partner, chain or hotel — or add a new one."
                                open={pickerOpen}
                                onOpenChange={(v) => (v ? setPickerOpen(true) : cancelEdit())}
                            />
                    </div>
                    {saveError && <span className="ppw-error-msg">{saveError}</span>}
                    <div className="occ-form-actions">
                        <button className="occ-btn-secondary" onClick={cancelEdit} disabled={saving}>Cancel</button>
                        <button className="ppw-btn-next" onClick={saveEdit} disabled={saving || !cs.selectedCredential || (selAboveHotel && !cs.hotelIdOverride.trim())}>
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </div>
            );
        }
        const name = comparison[`${side}_name`];
        return (
            <div className="occ-instance-block">
                <div className="occ-instance-row">
                    <div className="occ-instance-role">{roleLabel}</div>
                    {name ? (
                        <CredentialChip
                            name={name}
                            level={levelOf(comparison, side)}
                            meta={comparison[`${side}_hotelid_override`] ? `Hotel ID: ${comparison[`${side}_hotelid_override`]}` : undefined}
                            onChange={() => startEdit(side)}
                        />
                    ) : (
                        <button type="button" className="occ-select-btn" onClick={() => startEdit(side)}>+ Select instance</button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="pi-page">
            <div className="pi-header">
                <div className="pi-header-left">
                    <button className="pi-back-btn" onClick={() => navigate('/apps/opera-cloud-compare')}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Comparisons
                    </button>
                    <div className="pi-title-block">
                        <h1 className="pi-title">{comparison?.name || 'Comparison'}</h1>
                        <p className="pi-subtitle">Compare and repair configuration between the source and target OPERA Cloud instances</p>
                    </div>
                </div>
            </div>

            {error && <div className="ppw-error-msg" style={{ margin: '8px 0' }}>{error}</div>}

            <Tabs tabs={[{ key: 'compare', label: 'Compare', icon: <GitCompare size={20} /> }, SUPPORT_TAB]} active={tab} onChange={setTab} />

            {tab === 'support' ? (
                <ProductSupportTab product="OperaCloudCompare" productId={id} noun="this comparison"
                    context={() => ({ screen: 'comparison', comparisonId: id, comparisonName: comparison?.name })} />
            ) : loading ? (
                <div className="pi-empty"><p className="pi-empty-text">Loading…</p></div>
            ) : comparison ? (
                <>
                    <div className="occ-detail-instances" style={{ maxWidth: 720 }}>
                        <div className="occ-summary-row"><span>Hotel</span><strong>{comparison.hotel_name || '—'}</strong></div>
                        {renderInstanceRow('source', 'Source')}
                        {renderInstanceRow('target', 'Target')}
                    </div>

                    <div style={{ marginTop: 24 }}>
                        <OperaCloudCompareEngine
                            compareId={comparison.id}
                            sourceName={comparison.source_name}
                            targetName={comparison.target_name}
                        />
                    </div>
                </>
            ) : null}
        </div>
    );
};

export default OperaCloudCompareDetailPage;
